import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatKm, formatMoney, formatNumber, formatTime, t } from '@aquabox/shared';
import {
  useAddFuelLog,
  useDriverPerformance,
  useFuelLogs,
  useVehiclePositions,
  useVehicles,
} from '../api/queries';
import { Kpi } from '../components/Kpi';
import { periodLabel, type PeriodKey } from '../lib/periods';
import type { DriverPerformanceRow, VehiclePosition } from '../api/types';

type Range = 'today' | 'week' | 'all';

// Dönem seçenekleri ortak sözlükten; "Son 7 gün" ortak anahtarda last7'dir.
const RANGE_PERIOD: Record<Range, PeriodKey> = { today: 'today', week: 'last7', all: 'all' };

/**
 * "Konum paylaşımı" durumu (§3.15 C).
 *
 * Kapanmış/kapanış bekleyen seferlerde metin `DriverPerformanceRow`'dan (beyan/GPS
 * karşılaştırması); açık seferde CANLI konum `/api/tracking/positions`'tan gelir — ikisi ayrı
 * uçtur, biri diğerinin yerine geçmez (kural: sinyalsiz gün "0 km" değildir).
 */
function locationInfo(
  row: DriverPerformanceRow,
  position: VehiclePosition | undefined,
): { color: string; text: string } {
  if (row.status === 'CLOSED') {
    if (row.unexplained && row.diffKm !== null) {
      return {
        color: 'var(--danger)',
        text: t('drivers.locationVariance', {
          saat: row.closedAt ? formatTime(row.closedAt) : '—',
          n: Math.abs(row.diffKm),
        }),
      };
    }
    return {
      color: 'var(--text-secondary)',
      text: t('drivers.locationClosed', { saat: row.closedAt ? formatTime(row.closedAt) : '—' }),
    };
  }
  if (row.status === 'CLOSING') {
    return { color: 'var(--text-secondary)', text: t('drivers.locationWaiting') };
  }
  // Sefer açık (OPENED / LOADING / ON_ROUTE): canlı konum var mı bak.
  if (position && !position.stale && position.ageSeconds !== null) {
    return { color: 'var(--success)', text: t('drivers.locationLive', { n: position.ageSeconds }) };
  }
  if (position && position.ageSeconds !== null) {
    return {
      color: 'var(--text-secondary)',
      text: t('map.noSignalSince', { n: Math.round(position.ageSeconds / 60) }),
    };
  }
  return { color: 'var(--text-secondary)', text: t('map.neverSeen') };
}

/** "Konum denetimi" kartı satırı (§3.15 D) — Harita ekranındaki aynı mantığın kısa biçimi. */
function vehicleSignalLabel(row: VehiclePosition): string {
  if (row.ageSeconds === null) return t('map.neverSeen');
  if (!row.stale) return t('map.lastSeen', { n: row.ageSeconds });
  return t('map.noSignalSince', { n: Math.round(row.ageSeconds / 60) });
}

/**
 * Performans (§3.15) — `/personel` ekranının "Performans" sekmesi (21.10: `/soforler` buraya taşındı).
 *
 * BEYAN ve GPS AYRI SÜTUNDUR, biri diğerinin yerine geçmez. Fark eşiği sunucudan gelir
 * (kiracı ayarı), panelde sabit değildir. Sinyal kopan bir günde GPS km `null` gelir ve
 * ekranda "—" yazar: sinyalsiz gün "0 km gitti" demek değildir ve şoför telefonunun
 * çekmemesinden sorumlu tutulamaz.
 *
 * KPI'ların hiçbiri kesilmiş bir sayfadan toplanmaz: sefer listesi dönem bazlı tam gelir; mazot
 * gideri ve dolum adedi SUNUCUDAN gelen `total`/`count`tur (dolum listesi 200 satırla kesilir).
 * Para panelde toplanmaz ve bölünmez (kural 4).
 */
export function DriverPerformance() {
  const [range, setRange] = useState<Range>('today');
  const performance = useDriverPerformance(range);
  const fuel = useFuelLogs(range);
  const vehicles = useVehicles();
  const positions = useVehiclePositions();
  const addFuel = useAddFuelLog();
  const [formOpen, setFormOpen] = useState(false);
  // Ana tablo 5 sütun; satıra tıklayınca kalan alanlar detay satırında açılır.
  const [openTripId, setOpenTripId] = useState<string | null>(null);

  const rows = performance.data?.items ?? [];
  const positionByVehicle = new Map((positions.data ?? []).map((p) => [p.vehicle.id, p]));
  const threshold = performance.data?.threshold ?? 10;
  const totalKm = rows.reduce((sum, r) => sum + (r.gpsKm ?? 0), 0);
  // Mazot gideri de dolum ADEDİ de SUNUCUDAN gelir (`FuelLogList.total` / `.count`): `items` en
  // fazla 200 satırdır, panel kendi toplarsa ya da sayarsa kart uyarı vermeden eksik gösterir —
  // 5 araç × günde ~1 dolumla tavan ~40 günde aşılıyor. Alan yoksa değer BİLİNMİYOR demektir;
  // uydurulmuş rakam yerine tire yazılır, etiket hiç yazılmaz.
  const fuelTotal = fuel.data?.total;
  const fuelCount = fuel.data?.count;
  const unexplained = rows.filter((r) => r.unexplained).length;

  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 760, margin: 0 }}>
        {t('drivers.intro', { threshold })}
      </p>

      <div style={{ display: 'flex', gap: 8 }}>
        {(['today', 'week', 'all'] as Range[]).map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            aria-pressed={r === range}
            style={{
              borderRadius: 'var(--r-pill)',
              padding: '10px 15px',
              fontWeight: 600,
              fontSize: 13,
              background: r === range ? 'var(--primary)' : 'var(--surface)',
              border: `1px solid ${r === range ? 'var(--primary)' : 'var(--border)'}`,
              color: r === range ? '#fff' : 'var(--text-secondary)',
            }}
          >
            {periodLabel(RANGE_PERIOD[r])}
          </button>
        ))}
      </div>

      <section className="kpi-grid" style={{ '--kpi-gap': '16px' } as React.CSSProperties}>
        {/*
          "Ortalama ₺/km" KPI'si gizlendi: ₺/km sunucudan gelmiyor (dönem ortalaması yok) ve para
          bölmesi panelde yapılamaz (kural 4). Yer tutucu "—" + "yakında" kullanıcıya iş değil
          eksiklik gösterirdi; metrik gelene kadar kart yok.
        */}
        <Kpi label={t('drivers.kpiKm')} value={formatKm(totalKm)} note={t('drivers.kpiKmNote')} />
        <Kpi
          label={t('drivers.kpiFuel')}
          value={fuelTotal === undefined ? '—' : formatMoney(fuelTotal)}
          note={fuelCount === undefined ? undefined : t('drivers.kpiFuelNote', { n: fuelCount })}
        />
        <Kpi
          label={t('drivers.kpiUnexplained')}
          value={unexplained}
          accent={unexplained > 0 ? 'warning' : undefined}
          note={t('drivers.kpiUnexplainedNote', { threshold })}
        />
      </section>

      <section className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="drivers-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {(
                [
                  { key: 'drivers.colDriver', align: 'left', className: undefined },
                  { key: 'drivers.colDeliveries', align: 'right', className: undefined },
                  { key: 'drivers.colDistance', align: 'right', className: undefined },
                  { key: 'drivers.colCollected', align: 'right', className: undefined },
                  {
                    key: 'drivers.colLocation',
                    align: 'left',
                    className: 'drivers-location-cell',
                  },
                ] as const
              ).map((col) => (
                <th
                  key={col.key}
                  className={col.className}
                  style={{
                    textAlign: col.align,
                    padding: '12px 16px',
                    fontSize: 12.5,
                    color: 'var(--text-secondary)',
                    fontWeight: 600,
                    borderBottom: '1px solid var(--border)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(col.key as Parameters<typeof t>[0])}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{ padding: 20, color: 'var(--text-secondary)', fontSize: 14 }}
                >
                  {t('drivers.empty')}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <Row
                key={row.tripId}
                row={row}
                position={positionByVehicle.get(row.vehicleId)}
                expanded={openTripId === row.tripId}
                onToggle={() => setOpenTripId((id) => (id === row.tripId ? null : row.tripId))}
              />
            ))}
          </tbody>
        </table>
      </section>

      {/* Alt grid §3.15 D: Mazot dolumları (1fr) + Konum denetimi (320px). */}
      <section
        className="section-grid"
        style={{ '--section-cols': '1fr 320px', alignItems: 'start' } as React.CSSProperties}
      >
        <div
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 16 }}>{t('drivers.fuelTitle')}</strong>
            <button className="btn-secondary" onClick={() => setFormOpen((v) => !v)}>
              {formOpen ? t('common.cancel') : t('drivers.addFuel')}
            </button>
          </div>

          {formOpen && (
            <FuelForm
              vehicles={(vehicles.data?.items ?? []).map((v) => ({ id: v.id, plate: v.plate }))}
              busy={addFuel.isPending}
              onSubmit={(body) => {
                addFuel.mutate(body, { onSuccess: () => setFormOpen(false) });
              }}
            />
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {(fuel.data?.items ?? []).length === 0 && (
                <tr>
                  <td style={{ padding: '10px 0', color: 'var(--text-secondary)', fontSize: 13.5 }}>
                    {t('drivers.noFuel')}
                  </td>
                </tr>
              )}
              {(fuel.data?.items ?? []).map((log) => (
                <tr key={log.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 0', fontSize: 13.5, width: 80 }}>
                    {formatTime(log.occurredAt)}
                  </td>
                  <td style={{ padding: '10px 0', fontSize: 13.5, fontWeight: 600, width: 130 }}>
                    {log.vehicle.plate}
                  </td>
                  <td style={{ padding: '10px 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                    {log.station ?? t('drivers.noStation')}
                  </td>
                  <td style={{ padding: '10px 0', fontSize: 13.5, textAlign: 'right', width: 90 }}>
                    {log.liters} L
                  </td>
                  <td
                    style={{
                      padding: '10px 0',
                      fontSize: 13.5,
                      textAlign: 'right',
                      width: 110,
                      fontWeight: 600,
                    }}
                  >
                    {formatMoney(log.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <strong style={{ fontSize: 16 }}>{t('drivers.locationAuditTitle')}</strong>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
            {t('drivers.locationAuditHint')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(positions.data ?? []).map((p) => (
              <div
                key={p.vehicle.id}
                style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: p.active && !p.stale ? 'var(--success)' : 'var(--text-secondary)',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 700, width: 96, flexShrink: 0 }}>{p.vehicle.plate}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{vehicleSignalLabel(p)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({
  row,
  position,
  expanded,
  onToggle,
}: {
  row: DriverPerformanceRow;
  position: VehiclePosition | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const info = locationInfo(row, position);
  const diffKm = row.diffKm;
  const cell = {
    padding: '12px 16px',
    fontSize: 13.5,
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  };
  return (
    <>
      <tr
        style={{
          borderTop: '1px solid var(--border)',
          background: row.unexplained ? 'rgba(232,163,23,.07)' : undefined,
        }}
      >
        <td style={{ padding: '12px 16px', fontSize: 13.5 }}>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            style={{
              display: 'block',
              padding: 0,
              border: 0,
              background: 'none',
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>{row.driverName}</span>
            <span
              style={{ display: 'block', color: 'var(--primary)', fontWeight: 700, fontSize: 12.5 }}
            >
              {row.plate}
            </span>
          </button>
        </td>
        <td style={cell}>{row.deliveries}</td>
        {/* Sinyalsiz gün "0 km" DEĞİLDİR: değer yoksa tire gösterilir. */}
        <td style={{ ...cell, color: row.gpsKm === null ? 'var(--text-secondary)' : undefined }}>
          {row.gpsKm === null ? '—' : formatKm(row.gpsKm)}
        </td>
        <td style={cell}>{formatMoney(row.collected)}</td>
        <td
          className="drivers-location-cell"
          style={{ padding: '12px 16px', fontSize: 13, whiteSpace: 'nowrap' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: info.color }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: info.color,
                flexShrink: 0,
              }}
            />
            {info.text}
          </span>
        </td>
      </tr>
      {expanded ? (
        <tr style={{ background: 'var(--page-bg)' }}>
          <td colSpan={5} style={{ padding: '12px 16px 16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <DetailItem label={t('drivers.colJugs')} value={String(row.jugs)} />
              <DetailItem
                label={t('drivers.colDeclared')}
                value={row.declaredKm === null ? '—' : formatKm(row.declaredKm)}
              />
              <DetailItem
                label={t('drivers.colDiff')}
                value={
                  diffKm === null ? '—' : `${diffKm > 0 ? '+' : ''}${formatNumber(diffKm, 1)} km`
                }
                color={
                  diffKm === null
                    ? 'var(--text-secondary)'
                    : row.unexplained
                      ? 'var(--danger)'
                      : 'var(--success)'
                }
              />
              <DetailItem
                label={t('drivers.colFuel')}
                value={Number(row.fuelAmount) > 0 ? formatMoney(row.fuelAmount) : '—'}
              />
              <DetailItem label={t('drivers.colPer100')} value={row.litersPer100Km ?? '—'} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                className="btn"
                style={{ fontSize: 12.5, padding: '6px 10px' }}
                onClick={() => void navigate('/harita')}
              >
                {t('drivers.viewRoute')}
              </button>
              {diffKm !== null ? (
                // WhatsApp mesajı GÖNDERİLMİYOR — apps/api ve apps/panel içinde whatsapp entegrasyonu
                // yok. Sahte "mesaj hazırlandı" toastı yerine pasif gösterilir; Layout.tsx sidebar'daki
                // `disabled` menü öğesiyle aynı desen (panel.comingSoon).
                <button
                  className="btn"
                  style={{ fontSize: 12.5, padding: '6px 10px' }}
                  disabled
                  title={t('panel.comingSoon')}
                >
                  {t('drivers.ask')}
                </button>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DetailItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 13.5, color, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </span>
  );
}

function FuelForm({
  vehicles,
  busy,
  onSubmit,
}: {
  vehicles: { id: string; plate: string }[];
  busy: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id ?? '');
  const [liters, setLiters] = useState('');
  const [amount, setAmount] = useState('');
  const [station, setStation] = useState('');

  const ready = vehicleId !== '' && Number(liters.replace(',', '.')) > 0 && amount.trim() !== '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || busy) return;
        onSubmit({
          vehicleId,
          liters: Number(liters.replace(',', '.')),
          amount,
          station: station.trim() === '' ? undefined : station.trim(),
          // Çift tıklama koruması: aynı anahtar ikinci dolumu yazmaz.
          clientId: `panel-fuel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
      }}
      className="form-grid"
      style={
        {
          background: 'var(--page-bg)',
          borderRadius: 12,
          padding: 16,
          '--form-cols': '180px 120px 140px 1fr auto',
          alignItems: 'end',
        } as React.CSSProperties
      }
    >
      <Field label={t('drivers.fieldVehicle')}>
        <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="input">
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.plate}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('drivers.fieldLiters')}>
        <input
          className="input"
          inputMode="decimal"
          value={liters}
          onChange={(e) => setLiters(e.target.value)}
          placeholder="42"
        />
      </Field>
      <Field label={t('drivers.fieldAmount')}>
        <input
          className="input"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="1.850,00"
        />
      </Field>
      <Field label={t('drivers.fieldStation')}>
        <input
          className="input"
          value={station}
          onChange={(e) => setStation(e.target.value)}
          placeholder="Petrol Ofisi · F-2291"
        />
      </Field>
      <button type="submit" className="btn-primary" disabled={!ready || busy}>
        {busy ? t('common.loading') : t('common.save')}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}
